import {
  DeiDisabilityIdentify,
  DeiNeurodivergenceIdentify,
  DeiStageActionResponsesFragment,
} from 'generated/graphql';
import { capitalize, omit } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@spotted-zebra-uk/ui-components';
import DeiCheckboxFormField from './DeiCheckboxFormField';
import DeiSelectFormField from './DeiSelectField';
import {
  checkboxFields,
  getDeiFieldsWithOptions,
  initializeCheckboxValues,
  initializeDeiValues,
  initializeShowOther,
  otherFieldId,
  removeErrors,
} from './StageActionDeiForm.helpers';
import {
  DeiErrors,
  deiPayloadKeys,
  DeiValues,
} from './StageActionDeiForm.interfaces';
import styles from './StageActionDeiForm.module.scss';

// Max characters for the text area, if you change this, you need to change the error message in the translation file as well
const MAX_CHARACTERS = 160;

const DeiFormFields = ({
  formFields,
  onSubmit,
  loadingSubmit,
  responses,
}: {
  formFields: ReturnType<typeof getDeiFieldsWithOptions>;
  onSubmit: (payload: { [key: string]: string | string[] }) => void;
  loadingSubmit: boolean;
  responses: DeiStageActionResponsesFragment;
}) => {
  const { t } = useTranslation();
  const [selectValues, setSelectValues] = useState<DeiValues>(
    initializeDeiValues(responses)
  );
  const [checkboxValues, setCheckboxValues] = useState<{
    [key: string]: string[] | string;
  }>(initializeCheckboxValues(responses));
  const [showOther, setShowOther] = useState<{
    [key: string]: boolean;
  }>(initializeShowOther(initializeCheckboxValues(responses)));
  // If previous responses exist for the "other" fields, the text areas should be visible but not focused, this is why we need to track the focus state separately
  const [focusOtherField, setFocusOtherField] = useState<{
    [key: string]: boolean;
  }>({ neurodivergenceIdentifyOther: false, disabilityIdentifyOther: false });
  const [errors, setErrors] = useState<DeiErrors>({});

  const handleChange = (
    newValue: { label: string; value: string },
    id: string
  ) => {
    if (newValue.value === '') {
      const updatedValues = omit(selectValues, id as deiPayloadKeys);
      setSelectValues(updatedValues);
      return;
    }

    if (errors && errors.hasOwnProperty(id)) {
      setErrors(removeErrors(errors, id as deiPayloadKeys));
    }
    setSelectValues(prevValues => ({ ...prevValues, [id]: newValue }));
  };

  const handleCheckboxChange = (value: string[], id: string) => {
    setCheckboxValues(prevValues => ({ ...prevValues, [id]: value }));
    let tempErrors = { ...errors };
    if (tempErrors && tempErrors.hasOwnProperty(id)) {
      tempErrors = removeErrors(tempErrors, id as deiPayloadKeys);
    }

    if (
      value?.includes(
        DeiNeurodivergenceIdentify.Other || DeiDisabilityIdentify.Other
      )
    ) {
      setShowOther(prevValues => ({ ...prevValues, [id]: true }));
      setFocusOtherField(prevValues => ({ ...prevValues, [id]: true }));
      tempErrors = removeErrors(tempErrors, otherFieldId(id));
    } else {
      setShowOther(prevValues => ({ ...prevValues, [id]: false }));
      setFocusOtherField(prevValues => ({ ...prevValues, [id]: false }));
    }
    setErrors(tempErrors);
  };

  const handleOtherChange = (value: string, id: string) => {
    let tempErrors = { ...errors };
    if (tempErrors && tempErrors.hasOwnProperty(id)) {
      tempErrors = removeErrors(tempErrors, id as deiPayloadKeys);
    }

    if (value === '') {
      const updatedValues = omit(checkboxValues, id as deiPayloadKeys);
      setCheckboxValues(updatedValues);
      return;
    }
    // Handle error for max characters in the text area
    if (value.length > MAX_CHARACTERS) {
      tempErrors = {
        ...tempErrors,
        [id]: t('common.textAreaErrorMaxCharacters', {
          maxChars: MAX_CHARACTERS,
        }),
      };
    }

    setErrors(tempErrors);

    setCheckboxValues(prevValues => ({
      ...prevValues,
      [id]: value,
    }));
  };

  const handleSubmitDeiForm = () => {
    const errorsTmp = { ...errors };
    const selectAnswerKeys = Object.keys(selectValues);
    const selectFieldsInForm = formFields.filter(
      field => !checkboxFields?.includes(field.id)
    );
    const checkboxFieldsInForm = formFields.filter(field =>
      checkboxFields?.includes(field.id)
    );

    // Handle select fields errors
    if (selectAnswerKeys.length <= selectFieldsInForm.length) {
      selectFieldsInForm.forEach(field => {
        if (!selectAnswerKeys?.includes(field.id)) {
          errorsTmp[field.id as deiPayloadKeys] = t('common.required');
        }
      });
    }

    // Handle checkbox fields errors
    checkboxFieldsInForm.forEach(field => {
      if (!checkboxValues[field.id]?.length) {
        errorsTmp[field.id as deiPayloadKeys] = t('common.checkboxError');
      }

      if (
        checkboxValues[field.id]?.includes(
          DeiNeurodivergenceIdentify.Other || DeiDisabilityIdentify.Other
        ) &&
        !checkboxValues[otherFieldId(field.id)]
      ) {
        errorsTmp[otherFieldId(field.id)] = t('common.textAreaError');
      }
      if (
        checkboxValues[field.id]?.includes(
          DeiNeurodivergenceIdentify.Other || DeiDisabilityIdentify.Other
        ) &&
        checkboxValues[otherFieldId(field.id)]?.length > MAX_CHARACTERS
      ) {
        errorsTmp[otherFieldId(field.id)] = t(
          'common.textAreaErrorMaxCharacters',
          { maxChars: MAX_CHARACTERS }
        );
      }
    });

    setErrors(errorsTmp);

    if (Object.keys(errorsTmp).length === 0) {
      let payload: { [key: string]: string | string[] } = {};
      // Add values from select field option to the payload
      Object.entries(selectValues).forEach(val => {
        payload[val[0]] = val[1]?.value;
      });

      // Add values from checkboxes to the payload
      payload = { ...payload, ...checkboxValues };
      onSubmit(payload);
    }
  };

  return (
    <>
      {formFields.map(field => {
        // "Other" fields do not come from the BE in the list of available form questions.
        // It is necessary to check if a past response exists in order to see if the text area should be displayed
        const otherFieldValue = checkboxValues[
          otherFieldId(field.id)
        ] as unknown as string;

        return checkboxFields.includes(field.id) ? (
          <DeiCheckboxFormField
            key={field.id}
            id={field.id}
            onChange={value => handleCheckboxChange(value, field.id)}
            options={field.options}
            value={checkboxValues[field.id] as string[]}
            error={errors[field.id as deiPayloadKeys] || ''}
            showOther={showOther[field.id]}
            onOtherChange={handleOtherChange}
            textAreaValue={otherFieldValue}
            textAreaError={errors[otherFieldId(field.id)] || ''}
            focusOtherField={focusOtherField[field.id]}
          />
        ) : (
          <DeiSelectFormField
            key={field.id}
            {...field}
            options={field.options}
            value={selectValues[field.id as deiPayloadKeys]}
            onChange={handleChange}
            error={errors[field.id as deiPayloadKeys]}
          />
        );
      })}
      <div className={styles.submit}>
        <Button
          disabled={loadingSubmit}
          loading={loadingSubmit}
          onClick={handleSubmitDeiForm}
        >
          {loadingSubmit
            ? `${t('common.submitting')}...`
            : capitalize(t('assessment.completeQuestionnaire'))}
        </Button>
      </div>
    </>
  );
};

export default DeiFormFields;
